import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {Component, forwardRef, Inject} from "@angular/core";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {AccessoryService} from "./accessory.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {FormGroup} from "@angular/forms";
import {toggleLoading} from "../misc/loading.decorator";
import {AvailableStandaloneAccessoryModel, StandaloneAccessoryGqlModel} from "./standalone-accessory.gql-model";

export interface EditAccessoryDialogModel {
  accessory?: StandaloneAccessoryGqlModel;
  accessoryId?: string;
  siteId?: string;
  assetId?: string;
}

export interface EditAccessoryDialogResultModel {
  accessory: StandaloneAccessoryGqlModel;
  saved: boolean;
}

@Component({
  templateUrl: "./edit-accessory-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class EditAccessoryDialogComponent
  extends DialogComponent<EditAccessoryDialogModel, EditAccessoryDialogResultModel>
  implements EditAccessoryDialogModel {
  loading: boolean = true;
  error: Error;
  result: {accessory: StandaloneAccessoryGqlModel, saved: boolean} = {accessory: undefined, saved: false};
  private doSave: boolean = true;

  accessoryId: string;
  siteId: string;
  assetId: string;

  accessory: StandaloneAccessoryGqlModel = new StandaloneAccessoryGqlModel({});

  form: FormGroup = this.accessory.formGroup;

  constructor(
    public dialogService: DialogService,
    @Inject(forwardRef(() => AccessoryService)) private accessoryService: AccessoryService,
  ) {
    super(dialogService);
  }

  @toggleLoading()
  @handleErrors()
  async loadForm() {
    if (this.accessory && (this.accessory.id || this.accessory.siteId)) {
      this.accessory = this.accessory.clone(); // don't edit original except in save
      this.accessoryId = this.accessory.id;
      this.doSave = false;
    } else if (this.accessoryId) {
      this.accessory = await this.accessoryService.getAccessory(this.accessoryId);
    } else {
      this.accessory = await this.accessoryService.getAccessory(undefined, this.siteId, this.assetId);
      this.accessory.formGroup.controls["siteId"].markAsDirty();
      this.accessory.formGroup.controls["assetId"].markAsDirty();
    }

    this.form = this.accessory.formGroup;

    this.result.accessory = this.accessory;

    const device = this.accessory.availableStandaloneAccessories.find(
      it => it.id === this.accessory.deviceId && it.productNumber === this.accessory.productNumber,
    );

    if (device) {
      this.form.controls["accessorySelection"].setValue(device);
    } else {
      this.form.controls["accessorySelection"].setValue(null);
    }

    this.form.controls["accessorySelection"].markAsDirty();
  }

  ready() {
    this.loadForm();
  }

  @toggleLoading()
  @handleErrors()
  async save(): Promise<EditAccessoryDialogResultModel> {
    this.result.accessory = this.accessory;

    if (this.doSave) {
      const {id, updated} = await this.accessoryService.saveAccessory(this.accessory);
      if (id && updated) this.result.accessory.id = id;
      this.result.saved = updated;
    } else {
      this.result.saved = true;
    }

    this.close();

    return this.result;
  }

  accessorySelectionChanged() {
    const value: AvailableStandaloneAccessoryModel | null =
      new AvailableStandaloneAccessoryModel(this.form.controls["accessorySelection"].value);

    if (value) {
      this.accessory.deviceKey = value.deviceKey;
      this.accessory.productDescription = value.productDescription;
      this.accessory.availableBulkOrderItems = value.availableBulkOrderItems || [];

      this.form.controls["deviceId"].setValue(value.id);
      this.form.controls["productNumber"].setValue(value.productNumber);

      if (
        this.accessory.useBulkOrdering &&
        this.accessory.availableBulkOrderItems.some(it => it.id === this.accessory.bulkOrderAccessoryId)
      ) {
        this.form.controls["bulkOrderAccessoryId"].setValue("");
        this.form.controls["useBulkOrdering"].setValue(false);
        this.form.controls["bulkOrderAccessoryId"].markAsDirty();
        this.form.controls["useBulkOrdering"].markAsDirty();
      }
    } else {
      this.accessory.deviceKey = "N/A";
      this.accessory.productDescription = "Unsupported accessory";
      this.accessory.availableBulkOrderItems = [];

      this.form.controls["deviceId"].setValue("");
      this.form.controls["productNumber"].setValue("Unknown");
      this.form.controls["bulkOrderAccessoryId"].setValue("");
      this.form.controls["useBulkOrdering"].setValue(false);
      this.form.controls["bulkOrderAccessoryId"].markAsDirty();
      this.form.controls["useBulkOrdering"].markAsDirty();
    }

    this.form.controls["deviceId"].markAsDirty();
    this.form.controls["productNumber"].markAsDirty();
  }

  updateBulkOrderData() {
    if (!this.accessory.availableBulkOrderItems.length) {
      this.form.controls["useBulkOrdering"].setValue(false);
      this.form.controls["useBulkOrdering"].markAsDirty();
      return;
    }

    if (this.accessory.useBulkOrdering) {
      const bulkOrder = this.accessory.availableBulkOrderItems.find(
        it => it.id === this.accessory.bulkOrderAccessoryId,
      );

      if (bulkOrder) {
        this.form.controls["orderNumber"].setValue(bulkOrder.orderNumber);
        this.form.controls["orderDate"].setValue(bulkOrder.orderDate);
        this.form.controls["orderNumber"].markAsDirty();
        this.form.controls["orderDate"].markAsDirty();
      }
    }
  }
}
